import React, { FunctionComponent, memo, useMemo } from "react";
import { useIntl } from "react-intl";
import { zip, sum, isEqual, some } from "lodash";

import { ElectroPrices, GasPrices, PredictionData, CostsEstimationData } from "./types";

import GroupHeading from "./GroupHeading";
import RowOfValues from "./RowOfValues";
import EstimatedCosts from "./EstimatedCost";



interface IProps {
  cellWidth?: number;
  cellPaddingX?: number;
  data: Record<string, any>;
}


const RowsOfValues: FunctionComponent<IProps> = memo(function RowsOfValues({
  cellWidth = 35,
  cellPaddingX = 0,
  data,
}) {
  const intl = useIntl();
  const prediction: PredictionData = data.prediction;
  const costs_estimation: CostsEstimationData = data.costs_estimation;
  const e_prices: ElectroPrices = data.e_prices;
  const g_prices: GasPrices = data.g_prices;
  const nowHour = useMemo<number>(() => prediction?.hour || 0, [prediction?.hour]);
  const nowHourIndex = useMemo<number>(() => prediction?.hours?.indexOf(nowHour), [nowHour, prediction?.hours]);

  const consumptionPrediction = useMemo(() => prediction ? zip(prediction.predicted_consumption, prediction.predicted_consumption_heating).map(sum) : [], [prediction]);

  const commonRowProps = useMemo(() => ({
    cellWidth,
    cellPaddingX,
    nowHourIndex,
  }), [cellWidth, cellPaddingX, nowHourIndex]);

  const goodBadColors = useMemo(() => ({
    // colors: ["#3ab860", "#E309"],
    colors: ["#3ab860", "transparent"],
  }), []);

  const badGoodColors = useMemo(() => ({
    // colors: ["#E309", "#3ab860"],
    colors: ["transparent", "#3ab860"],
  }), []);

  return (
    <>
      {prediction && <>
        <GroupHeading>
          {intl.formatMessage({
            id: "site__trading__automat__heading",
            defaultMessage: "Automat",
          })}
        </GroupHeading>
        <RowOfValues {...commonRowProps} {...badGoodColors} key="dc" label={intl.formatMessage({id: "site__trading__row__discharge_plan", defaultMessage: "charge plan"})} units="%" values={prediction.discharge_plan.map(_ => -_ * 100)} signedValues />
        <RowOfValues {...commonRowProps} key="cg" label={intl.formatMessage({id: "site__trading__row__no_charge", defaultMessage: "no charging"})} values={prediction.no_charge} withoutChart />
        <RowOfValues {...commonRowProps} key="dg" label={intl.formatMessage({id: "site__trading__row__no_discharge", defaultMessage: "no discharging"})} values={prediction.no_discharge} withoutChart />

        {costs_estimation && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__estimated_costs_heading",
              defaultMessage: "Estimated Costs",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="pe" label={intl.formatMessage({id: "site__trading__row__estimate_no_ems", defaultMessage: "without EMS"})} units="€" values={costs_estimation.no_ems}>
            <EstimatedCosts strategy="no-ems" costs={costs_estimation.no_ems_cost} />
          </RowOfValues>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="pb" label={intl.formatMessage({id: "site__trading__row__estimate_baseline", defaultMessage: "without battery"})} units="€" values={costs_estimation.baseline}>
            <EstimatedCosts strategy="baseline" costs={costs_estimation.baseline_cost} bests={costs_estimation.best_strategies} />
          </RowOfValues>
          {costs_estimation.arbitrage && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="ps" label={intl.formatMessage({id: "site__trading__row__estimate_arbitrage", defaultMessage: "arbitrage"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.arbitrage}>
              <EstimatedCosts strategy="arbitrage" costs={costs_estimation.arbitrage_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="psb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.arbitrage_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.cost_shaving && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pc" label={intl.formatMessage({id: "site__trading__row__estimate_cost_shaving", defaultMessage: "lower costs"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.cost_shaving}>
              <EstimatedCosts strategy="cost-shaving" costs={costs_estimation.cost_shaving_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pcb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.cost_shaving_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.steep_arbitrage && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pa" label={intl.formatMessage({id: "site__trading__row__estimate_steep_arbitrage", defaultMessage: "steep arbitrage"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.steep_arbitrage}>
              <EstimatedCosts strategy="steep-arbitrage" costs={costs_estimation.steep_arbitrage_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pab" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.steep_arbitrage_battery} withoutChart multiplier={-1} />
          </>}
          {some([
            costs_estimation.naive_hybrid_arbitrage,
            costs_estimation.naive_self_consumption,
            costs_estimation.naive_autarky,
          ]) && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__naive_experimental_estimated_costs_heading",
              defaultMessage: "Experimental Strategies (SoC-unaware)",
            })}
          </GroupHeading>
          </>}
          {costs_estimation.naive_hybrid_arbitrage && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pnh" label={intl.formatMessage({id: "site__trading__row__estimate_arbitrage", defaultMessage: "arbitrage"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.naive_hybrid_arbitrage}>
              <EstimatedCosts strategy="naive-hybrid-arbitrage" costs={costs_estimation.naive_hybrid_arbitrage_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pnhb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.naive_hybrid_arbitrage_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.naive_self_consumption && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pnsc" label={intl.formatMessage({id: "site__trading__row__estimate_self_consumption", defaultMessage: "self-consumption"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.naive_self_consumption}>
              <EstimatedCosts strategy="naive-self-consumption" costs={costs_estimation.naive_self_consumption_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pnscb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.naive_self_consumption_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.naive_autarky && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pna" label={intl.formatMessage({id: "site__trading__row__estimate_autarky", defaultMessage: "self-sufficiency"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.naive_autarky}>
              <EstimatedCosts strategy="naive-autarky" costs={costs_estimation.naive_autarky_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pnab" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.naive_autarky_battery} withoutChart multiplier={-1} />
          </>}
          {some([
            costs_estimation.hybrid_arbitrage,
            costs_estimation.self_consumption,
            costs_estimation.autarky,
          ]) && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__experimental_estimated_costs_heading",
              defaultMessage: "Experimental Strategies (SoC-aware)",
            })}
          </GroupHeading>
          </>}
          {costs_estimation.hybrid_arbitrage && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="ph" label={intl.formatMessage({id: "site__trading__row__estimate_arbitrage", defaultMessage: "arbitrage"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.hybrid_arbitrage}>
              <EstimatedCosts strategy="hybrid-arbitrage" costs={costs_estimation.hybrid_arbitrage_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="phb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.hybrid_arbitrage_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.self_consumption && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="psc" label={intl.formatMessage({id: "site__trading__row__estimate_self_consumption", defaultMessage: "self-consumption"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.self_consumption}>
              <EstimatedCosts strategy="self-consumption" costs={costs_estimation.self_consumption_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="pscb" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.self_consumption_battery} withoutChart multiplier={-1} />
          </>}
          {costs_estimation.autarky && <>
            <RowOfValues {...commonRowProps} {...goodBadColors} key="pau" label={intl.formatMessage({id: "site__trading__row__estimate_autarky", defaultMessage: "self-sufficiency"})} units="€" values={costs_estimation.baseline} corrections={costs_estimation.autarky}>
              <EstimatedCosts strategy="autarky" costs={costs_estimation.autarky_cost} bests={costs_estimation.best_strategies} />
            </RowOfValues>
            <RowOfValues {...commonRowProps} key="paub" units="kWh" label={intl.formatMessage({id: "site__trading__row__estimate_battery", defaultMessage: "→battery"})} values={costs_estimation.autarky_battery} withoutChart multiplier={-1} />
          </>}
        </>}

        {e_prices && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__electricity_prices__heading",
              defaultMessage: "Electricity Prices",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="ey" label={intl.formatMessage({id: "site__trading__row__buy", defaultMessage: "buy"})} units="€/MWh" values={e_prices.buy_kwh} />
          <RowOfValues {...commonRowProps} key="eb" label={intl.formatMessage({id: "site__trading__row__base", defaultMessage: "base"})} units="€/MWh" values={e_prices.base_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="es" label={intl.formatMessage({id: "site__trading__row__sell", defaultMessage: "sell"})} units="€/MWh" values={e_prices.sell_kwh} withoutChart />
        </>}

        {g_prices && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__gas_prices__heading",
              defaultMessage: "Gas Prices",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="gy" label={intl.formatMessage({id: "site__trading__row__buy", defaultMessage: "buy"})} units="€/MWh" values={g_prices.buy_kwh} />
          <RowOfValues {...commonRowProps} key="gb" label={intl.formatMessage({id: "site__trading__row__base", defaultMessage: "base"})} units="€/MWh" values={g_prices.base_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="ge" label={intl.formatMessage({id: "site__trading__row__in_electricity", defaultMessage: "→electricity"})} units="€/MWh" values={g_prices.electricity_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="gh" label={intl.formatMessage({id: "site__trading__row__in_heat", defaultMessage: "→heat"})} units="€/MWh" values={g_prices.heat_kwh} withoutChart />
        </>}

        <GroupHeading>
          {intl.formatMessage({
            id: "site__trading__prediction__heading",
            defaultMessage: "Prediction",
          })}
        </GroupHeading>
        <RowOfValues {...commonRowProps} {...goodBadColors} key="cs" label={intl.formatMessage({id: "site__trading__row__clouds", defaultMessage: "clouds"})} units="%" values={prediction.weather.map(_ => (1 - _) * 100)} />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="pp" label={intl.formatMessage({id: "site__trading__row__production__pv", defaultMessage: "production PV"})} units="kWh" values={prediction.predicted_production_pv} corrections={prediction.real_production_pv} />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="cp" label={intl.formatMessage({id: "site__trading__row__production__chp", defaultMessage: "production CHP"})} units="kWh" values={prediction.predicted_production_chp} corrections={prediction.real_production_chp} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="et" label={intl.formatMessage({id: "site__trading__row__carbon_emissions", defaultMessage: "CO<sub>2</sub> emissions"})} units="kg/kWh" values={prediction.predicted_carbon_emissions} corrections={prediction.real_carbon_emissions} multiplier={0.001} />
        <RowOfValues {...commonRowProps} key="ep" label={intl.formatMessage({id: "site__trading__row__carbon_emissions_pv", defaultMessage: "→PV"})} units="kg/kWh" values={prediction.predicted_carbon_emissions_pv} corrections={prediction.real_carbon_emissions_pv} multiplier={0.001} withoutChart />
        <RowOfValues {...commonRowProps} key="ec" label={intl.formatMessage({id: "site__trading__row__carbon_emissions_chp", defaultMessage: "→CHP"})} units="kg/kWh" values={prediction.predicted_carbon_emissions_chp} corrections={prediction.real_carbon_emissions_chp} multiplier={0.001} withoutChart />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="es" label={intl.formatMessage({id: "site__trading__row__carbon_savings", defaultMessage: "CO<sub>2</sub> savings"})} units="kg/kWh" values={prediction.predicted_carbon_savings} corrections={prediction.real_carbon_savings} multiplier={0.001} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="co" label={intl.formatMessage({id: "site__trading__row__consumption", defaultMessage: "consumption"})} units="kWh" values={consumptionPrediction} corrections={prediction.real_consumption} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="pd" label={intl.formatMessage({id: "site__trading__row__power_draw", defaultMessage: "grid power draw"})} units="kWh" values={prediction.predicted_power_draw} corrections={prediction.real_power_draw} />
      </>}
    </>
  );
}, isEqual);

export default RowsOfValues;
